export interface AnnualReviewRating {
  key: string;
  section: 1 | 2 | 3 | 4;
  title: string;
  description: string;
  whatItLooksLike?: string[];
}

export interface AnnualReviewSubItem {
  key: string;
  label: string;
}

export interface AnnualReviewTextPrompt {
  key: string;
  label: string;
  required?: boolean;
}

export const ANNUAL_REVIEW_RATINGS: AnnualReviewRating[] = [
  {
    key: 'q1_safety',
    section: 1,
    title: 'Safety (Non-Negotiable)',
    description:
      'Maintains active supervision, transitions safely, and prevents incidents through awareness and engagement.',
    whatItLooksLike: [
      'Eyes on children at all times',
      'Strong transition management',
      'Proactive vs reactive supervision',
    ],
  },
  {
    key: 'q2_compliance',
    section: 1,
    title: 'Compliance & Accountability',
    description: 'Follows all licensing, health, and company standards consistently and with integrity.',
    whatItLooksLike: ['Diapering / hygiene', 'Ratio awareness', 'Incident reporting', 'App logging accuracy'],
  },
  {
    key: 'q3_classroom_execution',
    section: 1,
    title: 'Classroom Execution (The “Show”)',
    description: 'Creates a clean, organized, engaging classroom that reflects AEA standards.',
    whatItLooksLike: ['Clean + inviting environment', 'Materials are intentional, not cluttered', 'Smooth daily flow'],
  },
  {
    key: 'q4_classroom_management',
    section: 1,
    title: 'Classroom Management',
    description: 'Leads or supports a structured, calm, and well-functioning classroom.',
    whatItLooksLike: ['Children are engaged', 'Transitions are smooth', 'Teacher is calm + in control'],
  },
  {
    key: 'q5_child_engagement',
    section: 1,
    title: 'Child Engagement & Experience',
    description: 'Builds meaningful interactions with children that support development and joy.',
    whatItLooksLike: ['Gets on child’s level', 'Engages vs supervises only', 'Creates positive experiences'],
  },
  {
    key: 'q6_teamwork',
    section: 2,
    title: 'Teamwork & Collaboration',
    description: 'Works positively with others and contributes to a supportive team environment.',
  },
  {
    key: 'q7_communication',
    section: 2,
    title: 'Communication',
    description: 'Communicates clearly, respectfully, and proactively with team and leadership.',
  },
  {
    key: 'q8_reliability',
    section: 2,
    title: 'Reliability & Attendance',
    description: 'Shows up consistently, on time, and ready to contribute.',
  },
  {
    key: 'q9_attitude',
    section: 2,
    title: 'Attitude & Professionalism',
    description: 'Maintains a positive, solution-oriented mindset—even during challenges.',
  },
  {
    key: 'q10_coachability',
    section: 3,
    title: 'Coachability',
    description: 'Accepts feedback, applies it, and shows improvement over time.',
  },
  {
    key: 'q11_adaptability',
    section: 3,
    title: 'Adaptability',
    description: 'Handles change, transitions, and challenges with flexibility.',
  },
  {
    key: 'q12_initiative',
    section: 3,
    title: 'Initiative & Ownership',
    description: 'Steps up, takes responsibility, and looks for ways to help without being asked.',
  },
  {
    key: 'q13_family_experience',
    section: 4,
    title: 'Family Experience & Connection',
    description: 'Builds trust and strong relationships with families.',
    whatItLooksLike: ['Warm, confident interactions', 'Speaks positively about child', 'Represents AEA well'],
  },
  {
    key: 'q14_steam',
    section: 4,
    title: 'STEAM & Learning Engagement',
    description: 'Supports hands-on, engaging learning experiences aligned with AEA philosophy.',
    whatItLooksLike: ['Leads activities OR supports teacher execution', 'Encourages curiosity and exploration'],
  },
  {
    key: 'q16_transitions_supervision',
    section: 4,
    title: 'Transitions & Supervision (High Risk Area)',
    description:
      'Maintains strong supervision during transitions and high-risk moments (playground, bathroom, hallway, etc.)',
  },
];

export const CORE_VALUES_SUBITEMS: AnnualReviewSubItem[] = [
  { key: 'q15a_innovation', label: 'Innovation – Tries new ideas' },
  { key: 'q15b_growth', label: 'Growth – Wants to improve' },
  { key: 'q15c_collaboration', label: 'Collaboration – Supports team' },
  { key: 'q15d_connection', label: 'Connection – Builds relationships' },
];

export const SECTION_EXAMPLES_KEYS = {
  section1: 'section1_examples',
  section2: 'section2_examples',
  section3: 'section3_examples',
  section4: 'section4_examples',
} as const;

export const SELF_REFLECTION_PROMPTS: AnnualReviewTextPrompt[] = [
  { key: 'biggest_impact', label: 'Where do you feel you make the biggest impact?' },
  { key: 'area_to_improve', label: 'What is one area you want to improve?' },
  { key: 'support_needed', label: 'What support would help you be more successful?' },
];

export const RATING_SCALE_LABELS = [
  { value: 5, label: 'Exceeds Expectations', description: 'Consistently goes above and beyond without being asked' },
  { value: 4, label: 'Strong', description: 'Meets expectations at a high level, reliable and consistent' },
  { value: 3, label: 'Meets Expectations', description: 'Performs role as expected' },
  { value: 2, label: 'Needs Improvement', description: 'Inconsistent or requires frequent support' },
  { value: 1, label: 'Unsatisfactory', description: 'Does not meet expectations' },
];

export const RATING_DIRECTOR_REVIEW = {
  topStrengthsCount: 2,
  opportunitiesForGrowthCount: 2,
};

export const ratingsForSection = (section: 1 | 2 | 3 | 4): AnnualReviewRating[] => {
  return ANNUAL_REVIEW_RATINGS.filter((q) => q.section === section);
};
